import React, { useState } from 'react';
import { PageHeader, useReveal, GlowDivider } from '../components/Shared';

const PILLARS = [
  {
    id:'modeling', tag:'01', color:'teal', title:'Multi-Scale Modeling',
    desc:'From quantum mechanics to continuum-scale simulations, our computational stack bridges every relevant length and time scale.',
    items:['DFT & molecular dynamics','Mesoscale phase-field models','Finite element structural analysis','Machine-learned force fields','High-throughput property screening'],
    icon:<path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>,
  },
  {
    id:'ai', tag:'02', color:'purple', title:'Materials Informatics & AI',
    desc:'We deploy graph neural networks, Bayesian optimization, and generative models to navigate multi-dimensional materials design spaces.',
    items:['Graph neural network property prediction','Bayesian active learning loops','Generative inverse design','Transfer learning across materials families','Uncertainty-aware candidate ranking'],
    icon:<path d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17H3a2 2 0 01-2-2V5a2 2 0 012-2h16a2 2 0 012 2v10a2 2 0 01-2 2h-2"/>,
  },
  {
    id:'robotics', tag:'03', color:'pink', title:'Autonomous Fabrication',
    desc:'Our 24/7 robotic synthesis pods autonomously execute synthesis protocols, measure properties, and update models without human intervention.',
    items:['6-axis robotic arm synthesis','Automated characterization suite','Real-time process monitoring','Closed-loop feedback control','Digital twin integration'],
    icon:<path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3-3a1 1 0 000-1.4l-1.6-1.6a1 1 0 00-1.4 0l-3 3zM2 22l4-4m0 0l6.3-6.3M6 18l-4 4m10.3-10.3l1.4 1.4M14 14l4 4m-4-4l-2.3 2.3"/>,
  },
  {
    id:'validation', tag:'04', color:'teal', title:'Validation & Testing',
    desc:'Every candidate material passes through a rigorous multi-stage validation pipeline before being declared manufacturing-ready.',
    items:['Automated electrochemical testing','In-situ X-ray characterization','Accelerated aging protocols','Statistical process control','Manufacturing readiness assessment'],
    icon:<path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>,
  },
];

const PUBLICATIONS = [
  { year:'2024', title:'Autonomous Discovery of High-Performance Solid Electrolytes via Closed-Loop Robotic Experimentation', journal:'Nature Materials', authors:'Chen, K. et al.', doi:'10.1038/s41563-024-xxxx' },
  { year:'2024', title:'Graph Neural Network-Accelerated Screening of 2D Material Candidates for Energy Storage', journal:'ACS Nano', authors:'Patel, R. et al.', doi:'10.1021/acsnano.4c-xxxx' },
  { year:'2023', title:'Multi-Scale Modeling Framework for Polymer Electrolyte Degradation Under Operational Conditions', journal:'Advanced Energy Materials', authors:'Liu, W. et al.', doi:'10.1002/aenm.2023xxxx' },
  { year:'2023', title:'Bayesian Optimization of Synthesis Parameters for Perovskite Solar Cell Absorbers', journal:'Joule', authors:'Adesanya, O. et al.', doi:'10.1016/j.joule.2023.xxxx' },
  { year:'2022', title:'Robotic High-Throughput Synthesis and Characterization of Lithium-Ion Cathode Materials', journal:'Science Advances', authors:'Tanaka, H. et al.', doi:'10.1126/sciadv.2022xxxx' },
];

const colorMap  = { teal:'#00ffff', purple:'#a78bfa', pink:'#f9a8d4' };
const bgMap     = { teal:'rgba(0,255,255,0.08)', purple:'rgba(139,92,246,0.09)', pink:'rgba(244,114,182,0.07)' };
const borderMap = { teal:'rgba(0,255,255,0.18)', purple:'rgba(139,92,246,0.22)', pink:'rgba(244,114,182,0.18)' };

export default function RD() {
  useReveal();
  const [active, setActive] = useState('modeling');
  const pillar = PILLARS.find(p => p.id === active);

  return (
    <>
      <PageHeader
        tag="Research & Development"
        title="Science That"
        accent="Scales"
        sub="Four integrated research pillars that take a material from first-principles simulation to factory-ready validation."
      />

      <GlowDivider />

      {/* Pipeline overview */}
      <section className="section">
        <div className="container">
          <div className="text-center mb-14 reveal">
            <span className="section-tag">The Pipeline</span>
            <h2 className="section-title">From Atoms to <span className="text-gradient">Assembly Line</span></h2>
          </div>

          {/* Tab selector */}
          <div className="flex flex-wrap gap-3 justify-center mb-10 reveal">
            {PILLARS.map(p => (
              <button key={p.id} onClick={() => setActive(p.id)}
                className="font-mono text-xs tracking-[2px] uppercase px-5 py-2.5 rounded-full border transition-all duration-300 cursor-pointer"
                style={{
                  fontFamily:'var(--font-mono)',
                  background: active===p.id ? bgMap[p.color] : 'rgba(255,255,255,0.03)',
                  color:      active===p.id ? colorMap[p.color] : 'var(--text-2)',
                  borderColor:active===p.id ? borderMap[p.color] : 'var(--border)',
                  boxShadow:  active===p.id ? `0 0 20px ${bgMap[p.color]}` : 'none',
                }}>
                {p.tag} — {p.title}
              </button>
            ))}
          </div>

          {/* Active pillar detail */}
          <div className="glass-card reveal" style={{padding:'52px 48px'}}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="font-mono text-xs tracking-[3px] uppercase mb-3 block" style={{color:colorMap[pillar.color]}}>{pillar.tag} / {pillar.title}</span>
                <h3 className="font-display font-bold text-[2rem] tracking-[-0.04em] mb-5">{pillar.title}</h3>
                <p className="text-sm font-light leading-relaxed mb-7" style={{color:'var(--text-2)'}}>{pillar.desc}</p>
                <a href="/contact" className="btn btn--primary">Collaborate on This</a>
              </div>
              <div>
                <ul className="flex flex-col gap-4">
                  {pillar.items.map((item,i) => (
                    <li key={i} className="flex items-center gap-4 p-4 rounded-xl border transition-all duration-300 hover:border-current"
                      style={{background:'rgba(255,255,255,0.02)',borderColor:'var(--border)'}}>
                      <span className="font-mono text-xs w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 font-bold"
                        style={{background:bgMap[pillar.color],color:colorMap[pillar.color],border:`1px solid ${borderMap[pillar.color]}`}}>
                        {String(i+1).padStart(2,'0')}
                      </span>
                      <span className="text-sm font-light" style={{color:'var(--text-2)'}}>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <GlowDivider />

      {/* All 4 pillars grid */}
      <section className="section section-alt">
        <div className="container">
          <div className="text-center mb-14 reveal">
            <span className="section-tag">Research Areas</span>
            <h2 className="section-title">Four Pillars, <span className="text-gradient">One Platform</span></h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {PILLARS.map((p,i) => (
              <div key={p.id} className="glass-card reveal p-10" style={{transitionDelay:`${i*0.1}s`,position:'relative'}}>
                <div className="absolute top-0 left-0 right-0 h-[2px] rounded-t-[18px]"
                  style={{background:`linear-gradient(90deg,transparent,${colorMap[p.color]},transparent)`}} />
                <div className="w-12 h-12 rounded-[14px] flex items-center justify-center mb-6"
                  style={{background:bgMap[p.color],color:colorMap[p.color],border:`1px solid ${borderMap[p.color]}`}}>
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">{p.icon}</svg>
                </div>
                <span className="font-mono text-xs tracking-[3px] uppercase mb-2 block" style={{color:colorMap[p.color]}}>{p.tag}</span>
                <h3 className="font-display font-bold text-xl tracking-[-0.03em] mb-3">{p.title}</h3>
                <p className="text-sm font-light leading-relaxed" style={{color:'var(--text-2)'}}>{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <GlowDivider />

      {/* Publications */}
      <section className="section">
        <div className="container">
          <div className="text-center mb-14 reveal">
            <span className="section-tag">Publications</span>
            <h2 className="section-title">Recent <span className="text-gradient">Research</span></h2>
          </div>
          <div className="flex flex-col gap-4">
            {PUBLICATIONS.map((pub,i) => (
              <div key={i} className="glass-card reveal p-7 flex gap-6 items-start" style={{transitionDelay:`${i*0.08}s`}}>
                <span className="font-mono text-[0.65rem] tracking-[2px] pt-1 flex-shrink-0" style={{color:'#00ffff'}}>{pub.year}</span>
                <div className="flex-1 min-w-0">
                  <h4 className="font-display font-semibold text-base tracking-[-0.02em] mb-1 leading-snug">{pub.title}</h4>
                  <p className="text-sm mb-1" style={{color:'var(--text-2)'}}>{pub.authors} · <em>{pub.journal}</em></p>
                  <a href={`https://doi.org/${pub.doi}`}
                    className="font-mono text-xs" style={{color:'rgba(0,255,255,0.5)'}}>DOI: {pub.doi}</a>
                </div>
                <svg className="w-5 h-5 flex-shrink-0 mt-0.5" style={{color:'var(--text-3)'}} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                </svg>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
